import { useEffect } from 'react';

/** GSAP entrance, scroll, count-up, magnetic buttons, and hero parallax. Skipped when reduced motion is on. */
export function useV2Motion(scopeRef, pathname) {
  useEffect(() => {
    const root = scopeRef.current;
    if (!root) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let ctx;
    let cancelled = false;
    const cleanups = [];

    (async () => {
      const { default: gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      if (cancelled || !scopeRef.current) return;
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        // Hero text (.gs-hero) rises in with a CSS animation from first paint and is never hidden,
        // so it can be the page's Largest Contentful Paint without waiting for this script.
        const panel = gsap.utils.toArray('.gs-panel');
        const pops = gsap.utils.toArray('.gs-pop');
        if (panel.length || pops.length) {
          const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
          if (panel.length) {
            tl.from(panel, { autoAlpha: 0, duration: 1, clearProps: 'opacity,visibility' });
          }
          if (pops.length) {
            tl.from(
              pops,
              {
                scale: 0.2,
                autoAlpha: 0,
                duration: 1,
                ease: 'back.out(1.9)',
                stagger: 0.09,
                clearProps: 'opacity,visibility',
              },
              panel.length ? '-=0.8' : 0,
            );
          }
        }

        // Scroll reveals animate opacity only (never visibility), so off-screen headings and text
        // stay in the accessibility tree for screen readers, crawlers and audits.
        gsap.utils.toArray('.gs-up').forEach((el) => {
          gsap.from(el, {
            y: 44,
            opacity: 0,
            duration: 0.9,
            ease: 'power3.out',
            clearProps: 'transform,opacity,visibility',
            scrollTrigger: { trigger: el, start: 'top 90%', once: true },
          });
        });

        const cards = gsap.utils.toArray('.gs-card');
        if (cards.length) {
          gsap.set(cards, { opacity: 0, y: 56 });
          ScrollTrigger.batch(cards, {
            start: 'top 92%',
            once: true,
            onEnter: (batch) =>
              gsap.to(batch, {
                opacity: 1,
                y: 0,
                duration: 0.85,
                ease: 'power3.out',
                stagger: 0.1,
                clearProps: 'transform,opacity,visibility',
              }),
          });
        }

        // Clay icons spring in as their section scrolls into view (staggered per row).
        // Skips icons that already have their own motion (hero props, nav, dock, slider, step art).
        const icons = gsap.utils
          .toArray('.clay-icon')
          .filter((el) => !el.closest('.gs-heroarea, .hero-vis, .pp-hero-art, .nav-shell, .m-dock, .gs-icon, .gs-pop, .reveal3d, .r3-slider, .f1, .f2, .f3'));
        if (icons.length) {
          gsap.set(icons, { opacity: 0, scale: 0.55, rotate: -14, y: 14 });
          ScrollTrigger.batch(icons, {
            start: 'top 92%',
            once: true,
            onEnter: (batch) =>
              gsap.to(batch, {
                opacity: 1,
                scale: 1,
                rotate: 0,
                y: 0,
                duration: 0.9,
                delay: 0.12,
                ease: 'back.out(2.2)',
                stagger: 0.07,
                clearProps: 'transform,opacity,visibility',
              }),
          });
        }

        gsap.utils.toArray('.gs-icon').forEach((el) => {
          gsap.from(el, {
            scale: 0.4,
            rotate: -18,
            duration: 1,
            ease: 'elastic.out(1, 0.55)',
            clearProps: 'transform',
            scrollTrigger: { trigger: el, start: 'top 90%', once: true },
          });
        });

        const lis = gsap.utils.toArray('.gs-li');
        if (lis.length) {
          gsap.from(lis, {
            x: -30,
            opacity: 0,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power2.out',
            clearProps: 'transform,opacity,visibility',
            scrollTrigger: { trigger: lis[0], start: 'top 88%', once: true },
          });
        }

        gsap.utils.toArray('.gs-scrub').forEach((el) => {
          gsap.fromTo(
            el,
            { y: 50 },
            {
              y: -30,
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 1 },
            },
          );
        });

        gsap.utils.toArray('.gs-count').forEach((el) => {
          const node = el.firstChild;
          if (!node || node.nodeType !== Node.TEXT_NODE) return;
          const to = parseFloat(el.getAttribute('data-to')) || 0;
          const suffix = el.getAttribute('data-suffix') || '';
          const state = { v: 0 };
          gsap.to(state, {
            v: to,
            duration: 1.6,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 95%', once: true },
            onStart: () => {
              node.nodeValue = `0${suffix}`;
            },
            onUpdate: () => {
              node.nodeValue = `${Math.round(state.v)}${suffix}`;
            },
          });
        });
      }, root);

      if (window.matchMedia('(pointer: fine)').matches) {
        root.querySelectorAll('.btn-mag').forEach((btn) => {
          const xTo = gsap.quickTo(btn, 'x', { duration: 0.6, ease: 'power3.out' });
          const yTo = gsap.quickTo(btn, 'y', { duration: 0.6, ease: 'power3.out' });
          // Measure once on enter: reading the rect on every move includes the button's own
          // magnetic offset and hover scale, which made it chase itself and jitter.
          let cx = 0;
          let cy = 0;
          const enter = () => {
            const rect = btn.getBoundingClientRect();
            cx = rect.left + rect.width / 2 - (gsap.getProperty(btn, 'x') || 0);
            cy = rect.top + rect.height / 2 - (gsap.getProperty(btn, 'y') || 0);
          };
          const clamp = gsap.utils.clamp(-8, 8);
          const move = (e) => {
            if (!cx) enter();
            xTo(clamp((e.clientX - cx) * 0.16));
            yTo(clamp((e.clientY - cy) * 0.22));
          };
          const leave = () => {
            cx = 0;
            xTo(0);
            yTo(0);
          };
          btn.addEventListener('mouseenter', enter);
          btn.addEventListener('mousemove', move);
          btn.addEventListener('mouseleave', leave);
          cleanups.push(() => {
            btn.removeEventListener('mouseenter', enter);
            btn.removeEventListener('mousemove', move);
            btn.removeEventListener('mouseleave', leave);
          });
        });

        const hero = root.querySelector('.gs-heroarea');
        const items = [...root.querySelectorAll('.gs-par')].map((el) => ({
          depth: parseFloat(el.getAttribute('data-depth')) || 15,
          xTo: gsap.quickTo(el, 'x', { duration: 0.9, ease: 'power3' }),
          yTo: gsap.quickTo(el, 'y', { duration: 0.9, ease: 'power3' }),
        }));
        if (hero && items.length) {
          const onMove = (e) => {
            const rect = hero.getBoundingClientRect();
            const nx = (e.clientX - rect.left) / rect.width - 0.5;
            const ny = (e.clientY - rect.top) / rect.height - 0.5;
            items.forEach((item) => {
              item.xTo(nx * item.depth);
              item.yTo(ny * item.depth);
            });
          };
          const onLeave = () => {
            items.forEach((item) => {
              item.xTo(0);
              item.yTo(0);
            });
          };
          hero.addEventListener('mousemove', onMove);
          hero.addEventListener('mouseleave', onLeave);
          cleanups.push(() => {
            hero.removeEventListener('mousemove', onMove);
            hero.removeEventListener('mouseleave', onLeave);
          });
        }
      }

      requestAnimationFrame(() => ScrollTrigger.refresh());

      // Images, maps and lazy sections change page height after setup; keep trigger positions
      // in sync so cards below them still reveal instead of staying hidden.
      let refreshTimer = 0;
      const ro = new ResizeObserver(() => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
      });
      ro.observe(root);
      cleanups.push(() => {
        ro.disconnect();
        clearTimeout(refreshTimer);
      });
    })();

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
      if (ctx) ctx.revert();
    };
  }, [scopeRef, pathname]);
}
